import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '../contexts/AuthContext';
import ProfilePhotoUpload from './ui/ProfilePhotoUpload';
import { OptimizedImage } from "@/components/ui/OptimizedImage";

interface DojoSettings {
  dojo_name: string;
  dojo_logo_url: string;
}

const DojoSettings: React.FC = () => {
  useUiLanguage();
  const { t } = useTranslation(['admin', 'common']);
  const { profile } = useAuth();
  const [settings, setSettings] = useState<DojoSettings>({
    dojo_name: '',
    dojo_logo_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    if (!profile?.dojo_id) return;

    try {
      setLoading(true);
      // Load dojo-specific settings
      const { data: dojoData } = await supabase
        .from('dojo_specific_settings')
        .select('dojo_name, logo_url')
        .eq('dojo_id', profile.dojo_id)
        .maybeSingle();

      if (dojoData) {
        setSettings({
          dojo_name: dojoData.dojo_name || '',
          dojo_logo_url: dojoData.logo_url || ''
        });
      }
    } catch (error) {
      console.error('Error fetching dojo settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!profile?.dojo_id) return;

    if (profile?.role !== 'admin' && profile?.role !== 'super_user') {
      toast({
        title: t('common:messages.accessDenied', 'Access Denied'),
        description: t('admin:settings.adminOnly', 'Only administrators can update dojo settings.'),
        variant: "destructive"
      });
      return;
    }

    try {
      setSaving(true);

      // Save dojo settings
      const { error: dojoError } = await supabase
        .from('dojo_specific_settings')
        .upsert({
          dojo_id: profile.dojo_id,
          dojo_name: settings.dojo_name,
          logo_url: settings.dojo_logo_url || '/uploads/cc400511-d11c-444d-8341-92ff6e9ac918.png',
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'dojo_id'
        });

      if (dojoError) throw dojoError;

      toast({
        title: t('common:messages.settingsSaved', 'Settings Saved'),
        description: t('admin:settings.saveSuccess', 'Dojo settings have been updated successfully.')
      });

      // Refresh settings after save
      await fetchSettings();
    } catch (error) {
      console.error('Error saving dojo settings:', error);
      toast({
        title: t('common:messages.error'),
        description: t('admin:settings.saveError', 'Failed to save dojo settings.'),
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof DojoSettings, value: string) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (profile?.role !== 'admin' && profile?.role !== 'super_user') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('common:messages.accessDenied', 'Access Denied')}</CardTitle>
          <CardDescription>{t('admin:settings.adminOnly', 'Only administrators can access dojo settings.')}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{uiText("Dojo Branding")}</CardTitle>
        <CardDescription>
          {uiText("Customize your dojo's name and logo across the application")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dojo-name">{t('admin:settings.dojoName', 'Dojo Name')}</Label>
              <Input
                id="dojo-name"
                value={settings.dojo_name}
                onChange={(e) => handleInputChange('dojo_name', e.target.value)}
                placeholder={t('admin:settings.dojoNamePlaceholder', 'Enter your dojo name')}
                disabled={loading}
                className="input-polished"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('admin:settings.dojoLogo', 'Dojo Logo')}</Label>
              <div className="flex items-start gap-4">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                    {settings.dojo_logo_url ? (
                      <OptimizedImage
                        src={settings.dojo_logo_url}
                        alt={uiText("Dojo Logo")}
                        width={80}
                        height={80}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <div className="text-gray-400 dark:text-gray-500 text-xs text-center">
                        {uiText("No Logo")}</div>
                    )}
                  </div>
                  <ProfilePhotoUpload
                    currentPhotoUrl={settings.dojo_logo_url}
                    onPhotoUpdate={(url) => handleInputChange('dojo_logo_url', url)}
                    className="w-full standalone"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {uiText("Upload your dojo logo. Recommended size: 200x200px or larger.")}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {uiText("Supported formats: JPG, PNG, GIF, WebP (max 5MB)")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-border-subtle">
          <Button
            onClick={saveSettings}
            disabled={loading || saving}
            className="btn-dojo-primary"
          >
            {saving ? t('common:buttons.saving', 'Saving...') : t('common:buttons.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default DojoSettings;